// /app/frontend/src/pages/PublicPhotoWall.jsx
/**
 * Public Photo Wall - Page for participants to upload photos/videos
 * Accessed via QR code - Can be added to home screen (PWA)
 * 24H session persistence - no re-authentication needed
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { 
  Camera, Video, Upload, Check, ArrowLeft, Image as ImageIcon,
  Send, X, Loader2, User, Sparkles, Plus, Download, Share, Phone, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../lib/api';
import { getImageUrl } from '../lib/utils';

// Session duration: 24 hours
const SESSION_DURATION = 24 * 60 * 60 * 1000;

export default function PublicPhotoWall() {
  const { id: drawId } = useParams();
  const navigate = useNavigate();
  
  const [wallInfo, setWallInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Identification state
  const [identified, setIdentified] = useState(false);
  const [participantId, setParticipantId] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [pseudo, setPseudo] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [identifying, setIdentifying] = useState(false);
  
  // Upload state
  const [myPhotos, setMyPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  // PWA install state
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    loadWallInfo();
    checkSession();
    
    // Listen for PWA install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, [drawId]);

  const checkSession = () => {
    // Check if participant has a valid 24H session
    const savedSession = localStorage.getItem(`photowall_session_${drawId}`);
    if (savedSession) {
      try {
        const data = JSON.parse(savedSession);
        const sessionAge = Date.now() - data.timestamp;
        
        // Session valid for 24 hours
        if (sessionAge < SESSION_DURATION) {
          setParticipantId(data.participant_id);
          setFirstName(data.first_name);
          setPseudo(data.pseudo);
          setIdentified(true);
          loadMyPhotos(data.participant_id);
          return;
        }
      } catch (e) {
        // Invalid session, clear it
      }
      localStorage.removeItem(`photowall_session_${drawId}`);
    }
  };

  const loadWallInfo = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/public/draw/${drawId}/photo-wall`);
      setWallInfo(res.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Mur photos non disponible');
    } finally {
      setLoading(false);
    }
  };

  const loadMyPhotos = async (pid) => {
    try {
      const res = await api.get(`/public/draw/${drawId}/photo-wall/participant/${pid}`);
      setMyPhotos(res.data);
    } catch (error) {
      console.error('Error loading photos:', error);
    }
  };

  const validatePhone = (phoneNumber) => {
    // Remove spaces and check for valid French mobile number
    const cleaned = phoneNumber.replace(/\s/g, '');
    return cleaned.length >= 10 && /^(0|\+33)[67]/.test(cleaned);
  };

  const handleIdentify = async () => {
    if (!firstName.trim() || !pseudo.trim()) {
      toast.error('Veuillez remplir votre prénom et pseudo');
      return;
    }
    
    if (!phone.trim() || !validatePhone(phone)) {
      toast.error('Numéro de mobile invalide (ex: 06 XX XX XX XX)');
      return;
    }
    
    setIdentifying(true);
    try {
      const res = await api.post(`/public/draw/${drawId}/photo-wall/identify`, {
        first_name: firstName.trim(),
        pseudo: pseudo.trim(),
        email: email.trim() || null,
        phone: phone.trim()
      });
      
      setParticipantId(res.data.participant_id);
      setIdentified(true);
      
      // Save session with 24H expiry
      localStorage.setItem(`photowall_session_${drawId}`, JSON.stringify({
        participant_id: res.data.participant_id,
        first_name: firstName,
        pseudo: res.data.pseudo,
        timestamp: Date.now()
      }));
      
      // Load existing photos if any
      loadMyPhotos(res.data.participant_id);
      
      toast.success(`Bienvenue ${firstName} !`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur d\'identification');
    } finally {
      setIdentifying(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic'],
      ...(wallInfo?.allow_videos ? { 'video/*': ['.mp4', '.mov', '.webm'] } : {})
    },
    maxSize: 30 * 1024 * 1024,
    multiple: false
  });

  const handleUpload = async () => {
    if (!selectedFile || !participantId) return;
    
    // Check photo limit
    if (myPhotos.length >= (wallInfo?.max_photos || 5)) {
      toast.error(`Limite de ${wallInfo?.max_photos || 5} photos atteinte`);
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('participant_id', participantId);
      if (currentMessage.trim() && wallInfo?.allow_messages) {
        formData.append('message', currentMessage.trim());
      }
      
      const res = await api.post(`/public/draw/${drawId}/photo-wall/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });
      
      setMyPhotos(prev => [res.data, ...prev]);
      setSelectedFile(null);
      setPreviewUrl(null);
      setCurrentMessage('');
      setUploadProgress(0);
      
      toast.success('Photo partagée sur le mur !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur d\'upload');
    } finally {
      setUploading(false);
    }
  };

  const cancelSelection = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setCurrentMessage('');
  };

  const handleInstall = async () => {
    if (!deferredPrompt) {
      // Fallback for iOS
      toast.info('Appuyez sur le bouton Partager puis "Sur l\'écran d\'accueil"', { duration: 5000 });
      return;
    }
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      toast.success('Application installée !');
    }
    setDeferredPrompt(null);
    setCanInstall(false);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Mur Photos - ${wallInfo?.draw_title}`,
          text: 'Partagez vos photos !',
          url: window.location.href
        });
      } catch (e) {
        // User cancelled
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Lien copié !');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-black flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white/10 backdrop-blur border-white/20">
          <CardContent className="p-8 text-center">
            <X className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Mur photos indisponible</h2>
            <p className="text-white/70">{error}</p>
            <Button 
              className="mt-6" 
              variant="outline"
              onClick={() => navigate('/')}
            >
              Retour à l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-black flex flex-col">
      {/* Header with Logos */}
      <div className="bg-black/30 backdrop-blur border-b border-white/10 p-4 sticky top-0 z-10">
        <div className="max-w-lg mx-auto">
          {/* Logos Row */}
          <div className="flex items-center justify-center gap-4 mb-3">
            {wallInfo?.organizer_logo && (
              <img 
                src={getImageUrl(wallInfo.organizer_logo)} 
                alt={wallInfo?.organizer_name} 
                className="h-12 w-auto max-w-[120px] object-contain rounded-lg bg-white/10 p-1"
              />
            )}
            {wallInfo?.beneficiary_logo && wallInfo?.organizer_logo && (
              <span className="text-white/40 text-2xl font-light">×</span>
            )}
            {wallInfo?.beneficiary_logo && (
              <img 
                src={getImageUrl(wallInfo.beneficiary_logo)} 
                alt={wallInfo?.beneficiary_name} 
                className="h-12 w-auto max-w-[120px] object-contain rounded-lg bg-white/10 p-1"
              />
            )}
          </div>
          
          {/* Title Row */}
          <div className="flex items-center gap-4">
            <div className="flex-1 text-center">
              <h1 className="text-white font-bold">{wallInfo?.draw_title}</h1>
              <p className="text-white/60 text-xs">
                {wallInfo?.organizer_name}
                {wallInfo?.beneficiary_name && ` • ${wallInfo.beneficiary_name}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={handleShare}
                className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
              >
                <Share className="h-4 w-4 text-white" />
              </button>
              <Camera className="h-6 w-6 text-yellow-400" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 max-w-lg mx-auto p-4 pb-8 w-full">
        {/* Identification Form */}
        {!identified ? (
          <Card className="bg-white/10 backdrop-blur border-white/20">
            <CardContent className="p-6">
              {/* Instructions from organizer */}
              {wallInfo?.photo_wall_instructions && (
                <div className="mb-6 p-4 bg-yellow-500/20 border border-yellow-500/30 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <p className="text-white/90 text-sm">{wallInfo.photo_wall_instructions}</p>
                  </div>
                </div>
              )}

              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Identifiez-vous</h2>
                <p className="text-white/70 text-sm">
                  Une seule fois pour partager vos photos
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-white/80 text-sm mb-1 block">Prénom *</label>
                  <Input
                    placeholder="Votre prénom"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                </div>
                
                <div>
                  <label className="text-white/80 text-sm mb-1 block">Pseudo * (affiché sur l'écran)</label>
                  <Input
                    placeholder="Votre pseudo"
                    value={pseudo}
                    onChange={(e) => setPseudo(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                </div>
                
                <div>
                  <label className="text-white/80 text-sm mb-1 block flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Téléphone mobile *
                  </label>
                  <Input
                    type="tel"
                    placeholder="06 XX XX XX XX"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                </div>
                
                <div>
                  <label className="text-white/80 text-sm mb-1 block">Email (optionnel)</label>
                  <Input
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                </div>

                <Button 
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                  onClick={handleIdentify}
                  disabled={identifying}
                >
                  {identifying ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  C'est parti !
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Install App Banner */}
            <div className="mb-4 p-3 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <Download className="h-5 w-5 text-green-400" />
              </div>
              <div className="flex-1">
                <p className="text-white text-sm font-medium">Ajoutez à l'écran d'accueil</p>
                <p className="text-white/60 text-xs">Accès rapide pendant 24h</p>
              </div>
              <Button 
                size="sm"
                onClick={handleInstall}
                className="bg-green-500 hover:bg-green-600"
              >
                Installer
              </Button>
            </div>

            {/* Upload Section */}
            <Card className="bg-white/10 backdrop-blur border-white/20 mb-4">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                      <User className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-white font-medium">{pseudo}</span>
                  </div>
                  <span className="text-white/60 text-sm">
                    {myPhotos.length} / {wallInfo?.max_photos || 5} photos
                  </span>
                </div>

                {!selectedFile ? (
                  <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                      isDragActive 
                        ? 'border-yellow-400 bg-yellow-400/10' 
                        : 'border-white/30 hover:border-white/50'
                    }`}
                  >
                    <input {...getInputProps()} />
                    <div className="flex justify-center gap-4 mb-4">
                      <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center">
                        <Camera className="h-7 w-7 text-white" />
                      </div>
                      {wallInfo?.allow_videos && (
                        <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center">
                          <Video className="h-7 w-7 text-white" />
                        </div>
                      )}
                    </div>
                    <p className="text-white font-medium mb-1">
                      {isDragActive ? 'Déposez ici' : 'Ajouter une photo'}
                    </p>
                    <p className="text-white/60 text-sm">
                      {wallInfo?.allow_videos ? 'Photo ou vidéo (5s max)' : 'Formats: JPG, PNG, WEBP'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Preview */}
                    <div className="relative rounded-xl overflow-hidden bg-black/30">
                      {selectedFile.type.startsWith('video/') ? (
                        <video 
                          src={previewUrl} 
                          className="w-full max-h-64 object-contain"
                          controls
                          muted
                        />
                      ) : (
                        <img 
                          src={previewUrl} 
                          alt="Preview" 
                          className="w-full max-h-64 object-contain"
                        />
                      )}
                      <button
                        onClick={cancelSelection}
                        className="absolute top-2 right-2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center hover:bg-black/70"
                      >
                        <X className="h-4 w-4 text-white" />
                      </button>
                    </div>

                    {/* Message input */}
                    {wallInfo?.allow_messages && (
                      <div>
                        <Textarea
                          placeholder="Ajouter un message (optionnel)"
                          value={currentMessage}
                          onChange={(e) => setCurrentMessage(e.target.value)}
                          maxLength={200}
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/40 resize-none"
                          rows={2}
                        />
                        <p className="text-white/40 text-xs text-right mt-1">
                          {currentMessage.length}/200
                        </p>
                      </div>
                    )}

                    {/* Upload button */}
                    <Button 
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                      onClick={handleUpload}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Envoi... {uploadProgress}%
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Partager sur le mur
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* My Photos */}
            {myPhotos.length > 0 && (
              <div>
                <h3 className="text-white/80 font-medium mb-3 flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  Mes photos ({myPhotos.length})
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  {myPhotos.map(photo => (
                    <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden bg-white/10">
                      {photo.media_type === 'video' ? (
                        <video 
                          src={getImageUrl(photo.url)} 
                          className="w-full h-full object-cover"
                          muted
                        />
                      ) : (
                        <img 
                          src={getImageUrl(photo.url)} 
                          alt="" 
                          className="w-full h-full object-cover"
                        />
                      )}
                      {photo.hidden && (
                        <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                          <span className="text-white/60 text-xs">En attente</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer - Powered by TIREO */}
      <div className="mt-auto py-4 text-center">
        <div className="flex items-center justify-center gap-2 text-white/40 text-sm">
          <span>Propulsé par</span>
          <span 
            className="font-black text-lg bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent animate-pulse"
            style={{ fontFamily: 'Outfit' }}
          >
            TIREO
          </span>
        </div>
      </div>
    </div>
  );
}
